import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, RefreshCw, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { upcomingEvent as defaultEvent } from '../../mock';
import { ensureUids, stripUids, withUid } from '../../hooks/useArrayFieldIds';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const hydrate = (raw) => ({
  ...raw,
  features: ensureUids(raw.features),
  teamsCommitted: ensureUids(raw.teamsCommitted),
  contacts: ensureUids(raw.contacts),
});

const dehydrate = (raw) => ({
  ...raw,
  features: stripUids(raw.features),
  teamsCommitted: stripUids(raw.teamsCommitted),
  contacts: stripUids(raw.contacts),
});

const INPUT_STYLE = { width: '100%' };
const FIELD_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '0.75rem',
};

const SectionCard = ({ title, children }) => (
  <Card style={{ marginBottom: '1rem' }}>
    <CardHeader>
      <CardTitle style={{ color: '#FFD700' }}>{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const LabeledField = ({ label, children, fullWidth }) => (
  <label style={{ color: '#fff', display: 'block', ...(fullWidth ? { gridColumn: '1 / -1' } : {}) }}>
    <span style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#d4d4d4' }}>
      {label}
    </span>
    {children}
  </label>
);

const EventEditorTab = () => {
  const [event, setEvent] = useState(defaultEvent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/event/upcoming`);
      const data = await res.json();
      setEvent(hydrate(data.event ? { ...defaultEvent, ...data.event } : defaultEvent));
    } catch (err) {
      setStatus({ type: 'error', message: `Load failed: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (key) => (value) => setEvent((e) => ({ ...e, [key]: value }));

  const updateArrayItem = (key, idx, next) => setEvent((e) => {
    const arr = [...(e[key] || [])];
    arr[idx] = { ...next, _uid: arr[idx]?._uid || next._uid };
    return { ...e, [key]: arr };
  });
  const removeArrayItem = (key, idx) => setEvent((e) => {
    const arr = [...(e[key] || [])];
    arr.splice(idx, 1);
    return { ...e, [key]: arr };
  });
  const addArrayItem = (key, blank) => setEvent((e) => ({
    ...e,
    [key]: [...(e[key] || []), withUid(blank)],
  }));

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/event/upcoming`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dehydrate(event)),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: 'success', message: 'Event saved! Live on the site in a moment.' });
      } else {
        setStatus({ type: 'error', message: data.detail || 'Save failed.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: `Save failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#d4d4d4' }}>
        <RefreshCw className="animate-spin" /> Loading event…
      </div>
    );
  }

  return (
    <div data-testid="event-editor-tab" style={{ padding: '0 0.5rem' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <h2 style={{ color: '#FFD700', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Calendar size={22} /> Panthers Classic — Event Editor
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={load} className="import-btn" data-testid="event-editor-reload-btn">
            <RefreshCw size={16} /> Reload
          </Button>
          <Button onClick={save} disabled={saving} className="send-email-btn" data-testid="event-editor-save-btn">
            {saving ? <><RefreshCw size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> Save Changes</>}
          </Button>
        </div>
      </div>

      {status && (
        <div className={`reply-status ${status.type}`} data-testid="event-editor-status" style={{ marginBottom: '1rem' }}>
          {status.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {status.message}
        </div>
      )}

      {/* Basics */}
      <SectionCard title="Basics">
        <div style={FIELD_GRID_STYLE}>
          <LabeledField label="Title *" fullWidth>
            <input className="email-input" style={INPUT_STYLE} value={event.title} onChange={(e) => patch('title')(e.target.value)} data-testid="event-title" />
          </LabeledField>
          <LabeledField label="Subtitle">
            <input className="email-input" style={INPUT_STYLE} value={event.subtitle || ''} onChange={(e) => patch('subtitle')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Tagline (gold banner)">
            <input className="email-input" style={INPUT_STYLE} value={event.tagline || ''} onChange={(e) => patch('tagline')(e.target.value)} data-testid="event-tagline" />
          </LabeledField>
          <LabeledField label="Hook / italic line">
            <input className="email-input" style={INPUT_STYLE} value={event.hook || ''} onChange={(e) => patch('hook')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Date">
            <input className="email-input" style={INPUT_STYLE} value={event.date || ''} onChange={(e) => patch('date')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Time">
            <input className="email-input" style={INPUT_STYLE} value={event.time || ''} onChange={(e) => patch('time')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Venue">
            <input className="email-input" style={INPUT_STYLE} value={event.venue || ''} onChange={(e) => patch('venue')(e.target.value)} />
          </LabeledField>
          <LabeledField label="City / Address">
            <input className="email-input" style={INPUT_STYLE} value={event.city || ''} onChange={(e) => patch('city')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Age Groups">
            <input className="email-input" style={INPUT_STYLE} value={event.ageGroups || ''} onChange={(e) => patch('ageGroups')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Contact Email">
            <input className="email-input" style={INPUT_STYLE} value={event.email || ''} onChange={(e) => patch('email')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Description" fullWidth>
            <textarea className="email-textarea" style={INPUT_STYLE} rows={3} value={event.description || ''} onChange={(e) => patch('description')(e.target.value)} />
          </LabeledField>
        </div>
      </SectionCard>

      {/* Features */}
      <SectionCard title="Feature Badges (up to ~3 recommended)">
        {(event.features || []).map((f, i) => (
          <div key={f._uid} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
            <input className="email-input" placeholder="🏈" value={f.emoji} onChange={(e) => updateArrayItem('features', i, { ...f, emoji: e.target.value })} data-testid={`feature-emoji-${i}`} />
            <input className="email-input" placeholder="Title" value={f.title} onChange={(e) => updateArrayItem('features', i, { ...f, title: e.target.value })} data-testid={`feature-title-${i}`} />
            <input className="email-input" placeholder="Subtitle" value={f.subtitle} onChange={(e) => updateArrayItem('features', i, { ...f, subtitle: e.target.value })} data-testid={`feature-subtitle-${i}`} />
            <button type="button" onClick={() => removeArrayItem('features', i)} className="delete-btn" data-testid={`feature-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button onClick={() => addArrayItem('features', { emoji: '', title: '', subtitle: '' })} className="import-btn" data-testid="feature-add-btn">
          <Plus size={16} /> Add Feature
        </Button>
      </SectionCard>

      {/* Teams */}
      <SectionCard title="Represented Teams (chips shown on the flyer)">
        {(event.teamsCommitted || []).map((t, i) => (
          <div key={t._uid} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input className="email-input" style={{ flex: 1 }} value={t.value || ''} onChange={(e) => updateArrayItem('teamsCommitted', i, { ...t, value: e.target.value })} placeholder="Team Name (State)" data-testid={`team-input-${i}`} />
            <button type="button" onClick={() => removeArrayItem('teamsCommitted', i)} className="delete-btn" data-testid={`team-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button onClick={() => addArrayItem('teamsCommitted', '')} className="import-btn" data-testid="team-add-btn">
          <Plus size={16} /> Add Team
        </Button>
      </SectionCard>

      {/* Contacts */}
      <SectionCard title="Coach Contacts">
        {(event.contacts || []).map((c, i) => (
          <div key={c._uid} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input className="email-input" placeholder="Coach Name" value={c.name} onChange={(e) => updateArrayItem('contacts', i, { ...c, name: e.target.value })} data-testid={`contact-name-${i}`} />
            <input className="email-input" placeholder="(XXX) XXX-XXXX" value={c.phone} onChange={(e) => updateArrayItem('contacts', i, { ...c, phone: e.target.value })} data-testid={`contact-phone-${i}`} />
            <button type="button" onClick={() => removeArrayItem('contacts', i)} className="delete-btn" data-testid={`contact-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button onClick={() => addArrayItem('contacts', { name: '', phone: '' })} className="import-btn" data-testid="contact-add-btn">
          <Plus size={16} /> Add Coach
        </Button>
      </SectionCard>
    </div>
  );
};

export default EventEditorTab;
